import { Navigate, useParams } from 'react-router-dom';
import { useScopeNode } from '../hooks/useScopeNode';
import { Button } from './Button';
import {
  type ScopeSegment,
  tierForSegment,
  segmentMatchesTier,
  buildScopePath,
  normalizeLens,
} from '../lib/hierarchy-route';
import { Overview } from '../pages/Overview';
import { PhaseView } from '../pages/PhaseView';

/**
 * FR-1 dispatcher for the typed per-tier scope routes. Resolves the node from the
 * path (AC-1.2), redirects on a segment/tier mismatch (AC-1.5), and renders the
 * page by lens — Overview for `overview`, PhaseView for `discovery`/`delivery` —
 * passing the URL-resolved node down as props (the URL is the single source of
 * truth; there is no `TeamContext`). Tier dispatch (rollup vs leaf scorecard,
 * AC-1.3) happens inside those pages from the `node` prop.
 */
export function ScopeRoute({ segment }: { segment: ScopeSegment }) {
  const { number, lens: lensParam } = useParams<{ number: string; lens?: string }>();
  const lens = normalizeLens(lensParam);
  const expectedTier = tierForSegment(segment);
  const { node, teams, isLoading, needsAuth, error, notFound, refetch } = useScopeNode(number, expectedTier);

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 'var(--genai-space-lg)' }} data-testid="scope-loading">
        <p className="text-secondary">Loading…</p>
      </div>
    );
  }

  if (needsAuth) {
    return (
      <div className="card mt-md" style={{ padding: 'var(--genai-space-xl)', textAlign: 'center' }} data-testid="scope-auth-required">
        <p className="text-lg font-semibold text-primary">Please sign in to view data</p>
        <p className="text-secondary mt-sm">Your session has expired. Use the Sign in button in the top-right corner to re-authenticate.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--genai-space-lg)', borderColor: 'var(--genai-sem-error)' }} data-testid="scope-error">
        <p className="text-lg font-semibold" style={{ color: 'var(--genai-sem-error)' }}>Failed to load</p>
        <p className="text-secondary mt-sm">{error}</p>
        <Button variant="secondary" className="mt-md" onClick={refetch}>Retry</Button>
      </div>
    );
  }

  if (notFound || !node) {
    return <Navigate to="/" replace />;
  }

  // AC-1.5: the typed segment doesn't match the resolved node's tier → redirect
  // to the correct typed segment rather than mis-render.
  if (!segmentMatchesTier(segment, node.tier)) {
    return <Navigate to={buildScopePath(node, lens)} replace />;
  }

  return lens === 'overview'
    ? <Overview node={node} teams={teams} />
    : <PhaseView node={node} teams={teams} />;
}
